// ============================================================================
// Ask AI's machine card — a step on one floor machine (item #944, the fleet
// part; TCSD-0531 one tool per part, saved through the screen's own function).
// The server half is supabase/functions/am-copilot/fleet.ts.
//
// Every save here is the equipment screens' own (fleet/hooks/use-asset-location.ts),
// with source 'chat': Move (moveMachineByName — the database resolves the place
// and refuses to guess), "Yes, still here" (confirmMachineLocation) and the
// status sheet (setMachineStatus).
// ============================================================================

import { confirmMachineLocation, moveMachineByName, setMachineStatus } from '../fleet/hooks/use-asset-location';
import { notFoundMessage, parseDestinationRefusal } from '../fleet/destination-errors';
import { statusLabel } from '../fleet/machine-status';
import { refreshReaders } from '../../shared/save-path';
import { rpcErrorMessage } from '../../shared/rpc-error';
import type { FieldSpec } from './building-actions';

export const MACHINE_ACTIONS = ['move', 'confirm_here', 'set_status'] as const;
export type MachineAction = (typeof MACHINE_ACTIONS)[number];

/** A draft as the server persisted it (prepareMachineAction). */
export interface MachineDraft {
  machine_id: string;
  action: MachineAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface MachineCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: MachineDraft) => (d.context ?? {}) as Record<string, unknown>;
const broken = (s: string) => s === 'needs_repair' || s === 'out_for_repair';

/** The machine as the screens name it: its number and make. */
export const machineOf = (d: MachineDraft) => [d.machine_id, str(ctxOf(d).make)].filter(Boolean).join(' · ');

/** What the card shows for this draft, with the person's edits `f`. */
export function machineCard(d: MachineDraft, f: Record<string, unknown>): MachineCardSpec {
  const c = ctxOf(d);
  const info = [
    { label: 'Where it is now', value: str(c.where) || 'Not recorded' },
    { label: 'Status', value: statusLabel(str(c.status)) },
  ];
  const note: FieldSpec = { key: 'note', label: 'Note (optional)', kind: 'textarea' };
  switch (d.action) {
    case 'move':
      return {
        title: `Move to ${str(f.destination)}`,
        fields: [{ key: 'destination', label: 'Building or office', kind: 'text', required: true }, note],
        info,
        readBack: 'The place is matched by name. If the name fits more than one place, nothing moves and you are shown the choices.',
        doItLabel: 'Move',
        missing: str(f.destination).trim() ? null : 'Where is it now?',
      };
    case 'confirm_here':
      return { title: 'Yes, still here', fields: [], info, readBack: null, doItLabel: 'Yes, still here', missing: null };
    case 'set_status':
      return {
        title: `Set to ${statusLabel(str(f.status))}`,
        fields: [{ key: 'note', label: 'What is wrong with it? (optional)', kind: 'textarea' }],
        info,
        readBack: broken(str(f.status)) ? 'This sends the National Accounts Manager a direct message.' : null,
        doItLabel: 'Save',
        missing: null,
      };
  }
}

export interface MachineCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}

/** Save one machine draft through the equipment screen's own function. */
export async function commitMachineAction(d: MachineDraft, f: Record<string, unknown>): Promise<MachineCommitResult> {
  const card = machineCard(d, f);
  if (card.missing) return { ok: false, error: card.missing };
  const note = str(f.note).trim() || null;
  try {
    let msg: string;
    if (d.action === 'move') {
      msg = await moveMachineByName({ machineId: d.machine_id, destination: str(f.destination).trim(), note, source: 'chat' });
    } else if (d.action === 'confirm_here') {
      msg = await confirmMachineLocation(d.machine_id, 'chat');
    } else {
      msg = await setMachineStatus({ machineId: d.machine_id, status: str(f.status), note, source: 'chat' });
    }
    refreshReaders(['floor_machines']);
    return { ok: true, note: msg, target_row_id: d.machine_id, committed_payload: { ...f } };
  } catch (e) {
    // The database refuses to guess a place: say which places the name fits.
    const refusal = parseDestinationRefusal(e);
    if (refusal?.code === 'not_found') return { ok: false, error: notFoundMessage(refusal.query) };
    if (refusal?.code === 'ambiguous') {
      return { ok: false, error: `"${refusal.query}" fits more than one place: ${refusal.candidates.map((c) => c.name).join('; ')}. Edit the name to one of these and press Move again.` };
    }
    return { ok: false, error: rpcErrorMessage(e, 'That did not save.') };
  }
}
